import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { fmtNZD, BACKEND_URL, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BulkDeleteDialog from "@/components/BulkDeleteDialog";
import NumberInput from "@/components/NumberInput";
import DateInput from "@/components/DateInput";

const REASON_OPTIONS = ["Client request", "Site condition", "Design change", "Provisional sum adjustment", "Other"];

const BLANK_FORM = {
  description: "", reason: "Client request", amount_ex: 0, time_impact_days: 0,
  date_raised: "", breakdown: { labour: 0, materials: 0, subcontractors: 0, margin: 15 },
};

function VariationForm({ project, initial, isNew, onSaved, onClose, canEditApproved }) {
  const [f, setF] = useState(() => ({ ...BLANK_FORM, ...initial,
    breakdown: { labour: 0, materials: 0, subcontractors: 0, margin: 15, ...(initial?.breakdown || {}) } }));
  const [err, setErr] = useState(""); const [msg, setMsg] = useState(""); const [busy, setBusy] = useState(false);
  const showApprovedWarning = !isNew && initial?.status === "Approved";
  function setBreak(k, v) { setF({ ...f, breakdown: { ...f.breakdown, [k]: parseFloat(v || 0) } }); }
  async function save(e) {
    e.preventDefault();
    if (showApprovedWarning && !canEditApproved) { setErr("Only Admin can edit an approved variation"); return; }
    setBusy(true); setErr(""); setMsg("");
    const payload = { ...f, amount_ex: parseFloat(f.amount_ex || 0),
                      time_impact_days: parseInt(f.time_impact_days || 0) };
    try {
      if (isNew) await api.post(`/projects/${project.id}/variations`, payload);
      else       await api.patch(`/variations/${initial.id}`, payload);
      setMsg(isNew ? "Variation created ✓" : "Variation saved ✓");
      setTimeout(() => onSaved(), 400);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.message || "Save failed"));
    } finally { setBusy(false); }
  }
  return (
    <form onSubmit={save} className="card" data-testid={isNew ? "variation-new-form" : `variation-edit-form-${initial?.id}`}>
      <h3 className="text-silver mb-2">{isNew ? "New variation" : `Edit variation ${initial?.number || ""}`}</h3>
      {showApprovedWarning && (
        <div className="border border-amber-500/50 bg-amber-500/10 p-2 rounded text-sm text-amber-200 mb-3" data-testid="var-approved-warning">
          This variation is approved and already counts in the contract total.
          {!canEditApproved && " Only Admin can edit it."}
        </div>
      )}
      {err && <div className="text-red-500 text-sm mb-2" data-testid="var-form-error">{err}</div>}
      {msg && <div className="text-green-400 text-sm mb-2" data-testid="var-form-success">{msg}</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input className="input md:col-span-2" placeholder="Description" value={f.description} onChange={e=>setF({...f,description:e.target.value})} data-testid="var-f-description"/>
        <select className="input" value={f.reason} onChange={e=>setF({...f,reason:e.target.value})} data-testid="var-f-reason">
          {REASON_OPTIONS.map(r=> <option key={r}>{r}</option>)}
        </select>
        <label className="text-sm">Date raised
          <DateInput className="input" value={(f.date_raised||"").slice(0,10)} onChange={e=>setF({...f,date_raised:e.target.value})} data-testid="var-f-date"/></label>
        <label className="text-sm">Time impact (days)
          <NumberInput className="input" value={f.time_impact_days} onChange={e=>setF({...f,time_impact_days:parseInt(e.target.value||0)})} data-testid="var-f-time"/></label>
        <label className="text-sm">Labour $
          <NumberInput className="input" value={f.breakdown.labour || 0} onChange={e=>setBreak("labour", e.target.value)} data-testid="var-f-labour"/></label>
        <label className="text-sm">Materials $
          <NumberInput className="input" value={f.breakdown.materials || 0} onChange={e=>setBreak("materials", e.target.value)} data-testid="var-f-materials"/></label>
        <label className="text-sm">Subbies $
          <NumberInput className="input" value={f.breakdown.subcontractors || 0} onChange={e=>setBreak("subcontractors", e.target.value)} data-testid="var-f-subs"/></label>
        <label className="text-sm">Margin %
          <NumberInput className="input" value={f.breakdown.margin || 0} onChange={e=>setBreak("margin", e.target.value)} data-testid="var-f-margin"/></label>
        <label className="text-sm md:col-span-2">Amount ex GST
          <NumberInput className="input" value={f.amount_ex} onChange={e=>setF({...f,amount_ex:parseFloat(e.target.value||0)})} data-testid="var-f-amount"/></label>
      </div>
      <div className="mt-3 flex gap-2">
        <button className="btn-silver" disabled={busy} data-testid={isNew ? "var-save-new" : `var-save-edit-${initial?.id}`}>
          {busy ? "Saving…" : (isNew ? "Create" : "Save")}
        </button>
        <button type="button" className="btn-ghost" onClick={onClose} data-testid="var-cancel">Cancel</button>
      </div>
      {!isNew && (initial?.edit_history || []).length > 0 && (
        <div className="mt-4 text-xs opacity-70" data-testid={`var-history-${initial.id}`}>
          <div className="text-silver mb-1">Edit history</div>
          <ul className="space-y-0.5">
            {initial.edit_history.map((h, i) => (
              <li key={i}>
                {(h.at||"").slice(0,16).replace("T"," ")} · {h.by_user_name} ({h.by_role})
                {h.changed_fields?.length ? ` — ${h.changed_fields.join(", ")}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </form>
  );
}

export default function VariationsTab() {
  const { project, reload } = useOutletContext();
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [msg, setMsg] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  async function load() {
    const { data } = await api.get(`/projects/${project.id}/variations`);
    setList(data);
    setSelected(prev => new Set([...prev].filter(id => data.some(d => d.id === id))));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  async function markApproved(v) {
    await api.post(`/variations/${v.id}/decision`, { status: "Approved", note: "Approved in Hub" });
    setMsg(`Variation ${v.number} marked approved`); load(); reload?.();
  }
  async function send(v) {
    const { data } = await api.post(`/variations/${v.id}/send`);
    const link = window.location.origin + `/v/${data.token}`;
    window.prompt("Public approval link:", link);
    load();
  }
  async function del(v) {
    if (!window.confirm(`Delete draft variation ${v.number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/variations/${v.id}`);
      setMsg(`Deleted ${v.number}`); load(); reload?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    }
  }

  const isAdmin = user?.role === "Admin";
  const canEditApproved = isAdmin;
  const canEdit = (v) => v.status !== "Approved" || canEditApproved;
  const canDelete = (v) => v.status === "Draft";

  function toggleAll() {
    if (selected.size === list.length) setSelected(new Set());
    else setSelected(new Set(list.map(v => v.id)));
  }
  function toggleOne(id) {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  }

  const selectedList = list.filter(v => selected.has(v.id));
  const hasApproved = selectedList.some(v => v.status === "Approved");
  const totalApprovedAmount = selectedList.filter(v => v.status === "Approved")
    .reduce((s, v) => s + (parseFloat(v.amount_ex || 0)), 0);

  async function bulkConfirm({ confirm_text, financial_override }) {
    const r = await api.post("/variations/bulk-delete", {
      ids: Array.from(selected), confirm_text, financial_override,
    });
    setMsg(`Deleted ${r.data.soft_deleted} variations. Restorable from Projects → Deleted items for 30 days.`);
    setSelected(new Set());
    await load();
    reload?.();
  }

  const editing = list.find(v => v.id === editingId) || null;

  return (
    <div className="space-y-3" data-testid="variations-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-xl">Variations</h2>
        <div className="flex gap-2">
          {isAdmin && selected.size > 0 && (
            <button className="btn-ghost text-red-400 text-sm" data-testid="btn-bulk-delete-variations"
                    onClick={() => setBulkOpen(true)}>
              Delete selected ({selected.size})
            </button>
          )}
          <button className="btn-silver" onClick={() => setShowNew(!showNew)} data-testid="btn-new-variation">+ New Variation</button>
        </div>
      </div>
      {msg && <div className="text-green-400 text-sm" data-testid="var-list-msg">{msg}</div>}

      {showNew && (
        <VariationForm project={project} initial={BLANK_FORM} isNew canEditApproved={canEditApproved}
          onClose={()=>setShowNew(false)}
          onSaved={()=>{ setShowNew(false); load(); reload?.(); }}/>
      )}
      {editing && (
        <VariationForm project={project} initial={editing} isNew={false} canEditApproved={canEditApproved}
          onClose={()=>setEditingId(null)}
          onSaved={()=>{ setEditingId(null); load(); reload?.(); }}/>
      )}

      <div className="card">
        <table className="table"><thead><tr>
          {isAdmin && (
            <th className="w-8"><input type="checkbox" data-testid="var-select-all"
              checked={list.length > 0 && selected.size === list.length}
              onChange={toggleAll} aria-label="Select all"/></th>
          )}
          <th>No.</th><th>Description</th><th>Date</th><th>Amount ex GST</th><th>Status</th><th>Actions</th>
        </tr></thead>
          <tbody>{list.map(v => (
            <tr key={v.id} data-testid={`variation-${v.id}`}>
              {isAdmin && (
                <td><input type="checkbox" data-testid={`var-select-${v.id}`}
                  checked={selected.has(v.id)} onChange={() => toggleOne(v.id)}/></td>
              )}
              <td>{v.number}</td>
              <td>{v.description}</td>
              <td>{v.date_raised ? fmtDate(v.date_raised) : "—"}</td>
              <td>{fmtNZD(v.amount_ex)}</td>
              <td><span className="badge badge-grey">{v.status}</span></td>
              <td className="flex gap-2 flex-wrap">
                <a className="hub-link text-xs" href={`${BACKEND_URL}/api/variations/${v.id}/pdf`} target="_blank" rel="noreferrer">PDF</a>
                {canEdit(v) && (
                  <button className="text-xs hub-link" onClick={()=>setEditingId(v.id)} data-testid={`btn-edit-variation-${v.id}`}>Edit</button>
                )}
                {v.status === "Draft" && <button className="text-xs" onClick={()=>send(v)} data-testid={`btn-send-variation-${v.id}`}>Send</button>}
                {v.status !== "Approved" && <button className="text-xs text-green-400" onClick={()=>markApproved(v)} data-testid={`btn-approve-variation-${v.id}`}>Approve</button>}
                {canDelete(v) && (
                  <button className="text-xs text-red-400" onClick={()=>del(v)} data-testid={`btn-delete-variation-${v.id}`}>Delete</button>
                )}
              </td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={isAdmin ? "7" : "6"} className="opacity-60">No variations yet.</td></tr>}
          </tbody></table>
      </div>

      <BulkDeleteDialog open={bulkOpen} onClose={() => setBulkOpen(false)}
        count={selected.size} items={selectedList.map(v => `${v.number} · ${v.description} · ${fmtNZD(v.amount_ex)} · ${v.status}`)}
        totalAmount={totalApprovedAmount} hasApproved={hasApproved}
        onConfirm={bulkConfirm}
        labels={{ entity: "variations", noun: "variation" }}/>
    </div>
  );
}
